import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Download, Package, ArrowRight, Lock, Star } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import MembershipIDCard from "@/components/MembershipIDCard";

const SAMPLE_ID = {
  holder_name: "Amara Whitefeather",
  credential_id: "MFB-2026-0007",
  card_number: "7GIT 7A21 9C43 E5D8",
  member_since: "2026",
  class: "Indigenous Constituent",
  dob: "14 MAR 1988",
  region: "Great Lakes · N. America",
  bureau: "The Modern Freedmen Bureau",
  id_type: "International Identification",
  tax_status: "Tax Exempt",
  valid_thru: "NON-EXPIRING",
  compliance: "U.S. Digital ID · Global Authentication",
  undrip: "UN Declaration on the Rights of Indigenous Peoples · Compliant",
};
const SAMPLE_PHOTO = "https://images.unsplash.com/photo-1758600435036-0c958b8b9704?crop=entropy&cs=srgb&fm=jpg&q=85&w=400";

const Option = ({ icon: Icon, tag, title, price, features, cta, onClick, testid, highlight }) => (
  <div className={`border p-8 sm:p-10 h-full flex flex-col ${highlight ? "border-gold/50 bg-ink-surface" : "border-gold/15 bg-ink"}`}>
    <Icon size={28} className="text-gold" strokeWidth={1.3} />
    <p className="font-mono text-[10px] tracking-[0.26em] uppercase text-gold/60 mt-6">{tag}</p>
    <h3 className="font-serif text-3xl mt-1">{title}</h3>
    <p className="font-serif text-4xl text-gold-gradient mt-4">{price}</p>
    <ul className="mt-8 space-y-4 flex-1">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <Check size={16} className="text-gold mt-0.5 shrink-0" />
          <span className="font-sans text-sm text-[#C9C4BD]">{f}</span>
        </li>
      ))}
    </ul>
    <button onClick={onClick} data-testid={testid} className="mt-10 flex items-center justify-center gap-3 px-6 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">
      {cta} <ArrowRight size={14} />
    </button>
  </div>
);

export default function OrderID({ embedded }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/vault" : "/login");

  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow="Membership ID"
          title="Order your 7 Generations Membership ID."
          subtitle="Every verified constituent receives an instant digital ID. Order an embossed physical card to carry your standing with the Modern Freedmen Bureau."
        />
      )}

      <section className="py-20 bg-ink">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <Eyebrow>A Sample of Your Credential</Eyebrow>
              <h2 className="font-serif font-light text-4xl tracking-tight mt-5">The 7 Generations International ID.</h2>
              <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-5 max-w-md">
                Every card carries the gold compliance star and is recognized as an International
                Identification credential — tax exempt and non-expiring.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  ["Gold compliance star", "Aligned with U.S. Digital ID & global international authentication standards."],
                  ["International Identification", "A sovereign, cross-border credential of the 7 Generations Indigenous Trust."],
                  ["Tax Exempt", "Issued under the Trust's independent institutional framework."],
                  ["Non-Expiring", "Valid for life — anchored permanently to the registry."],
                  ["UNDRIP Compliant", "Recognized under the UN Declaration on the Rights of Indigenous Peoples."],
                ].map(([t, d]) => (
                  <li key={t} className="flex items-start gap-3">
                    <Star size={16} className="text-gold mt-0.5 shrink-0" />
                    <span className="font-sans text-sm text-[#C9C4BD]"><span className="text-[#F5F0EB]">{t}</span> — {d}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15} className="flex justify-center">
              <div className="relative" data-testid="sample-id-preview">
                <div className="absolute -inset-8 bg-[radial-gradient(circle,rgba(212,175,55,0.12)_0%,transparent_65%)] blur-xl" />
                <div className="relative">
                  <MembershipIDCard data={SAMPLE_ID} photoUrl={SAMPLE_PHOTO} />
                </div>
                <p className="font-mono text-[9px] tracking-[0.24em] uppercase text-gold/50 mt-4 text-center">Sample · Not a valid credential</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-10 pb-20 bg-ink">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-8">
            <Reveal>
              <Option
                icon={Download}
                tag="Instant"
                title="Digital ID"
                price="Free"
                features={[
                  "Issued instantly to verified members",
                  "Blockchain-anchored credential ID",
                  "Downloadable, print-ready card",
                  "Lifetime validity",
                ]}
                cta={user ? "Open in the Vault" : "Sign in to claim"}
                onClick={go}
                testid="order-digital-cta"
              />
            </Reveal>
            <Reveal delay={0.12}>
              <Option
                icon={Package}
                tag="Mailed to you"
                title="Physical ID Card"
                price="$49.00"
                highlight
                features={[
                  "Embossed metallic card",
                  "Includes everything in Digital ID",
                  "Secure worldwide shipping",
                  "Tamper-evident credential",
                ]}
                cta={user ? "Order in the Vault" : "Sign in to order"}
                onClick={go}
                testid="order-physical-cta"
              />
            </Reveal>
          </div>

          {!user && (
            <Reveal delay={0.2}>
              <div className="flex items-center justify-center gap-3 mt-12 text-[#7a766f]">
                <Lock size={15} className="text-gold/60" />
                <p className="font-sans text-sm">Membership IDs are issued only to verified members. Sign in to The Vault to continue.</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
